import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import { sortBy } from 'lodash-es';
import head from 'lodash-es/head';
import isEmpty from 'lodash-es/isEmpty';
import { Observable, of } from 'rxjs';

import { extractAcronym } from 'domain/domain.utils';
import {
	UserPartnerRightAccess,
	UserPartnerRightTarget,
} from 'infrastructure/common/api.types';
import { IOrganization, Organization } from 'organization/organization';
import { ITask } from 'task/task';
import { UserMoodFrequency } from 'user/model/user-mood-frequency';
import { UserRole } from 'user/model/user-role';
import { UserStatus } from 'user/model/user-status';
import { UserPartner } from 'user/partner/user-partner';
import { UserModulePush } from 'user/user-module-push';

import { IUserAvailability, UserAvailability } from './user-availability';
import { IUserMetadata, UserMetadata } from './user-metadata';
import { IUserReference, UserReference } from './user-reference';

export interface IUser extends IEntity {
	readonly role: UserRole;
	readonly hasMfa: boolean;
	readonly coachId: string;
	readonly timeZone: string;
	readonly lastName: string;
	readonly firstName: string;
	readonly status: UserStatus;
	readonly pictureUrl: string;
	readonly phoneNumber: string;
	readonly partnersId: string[];
	readonly emailAddress: string;
	readonly intercomHash: string;
	readonly observersId: string[];
	readonly organizationId: string;
	readonly companySectors: string;
	readonly metadata: IUserMetadata;
	readonly diplomas: ReadonlyArray<IUserReference>;
	readonly certificates: ReadonlyArray<IUserReference>;
	readonly availabilities: ReadonlyArray<IUserAvailability>;
	readonly modulePushes: ReadonlyArray<UserModulePush>;
	readonly coach$: Observable<IUser>;
	readonly organization$: Observable<IOrganization>;
	readonly partners$: Observable<IOrganization[]>;
	readonly partners: UserPartner[];
	readonly name: string;
	readonly acronym: string;
	readonly sectors: string[];
	readonly lastReference: string;
	readonly isCoachee: boolean;
	readonly isCoach: boolean;
	readonly isAdmin: boolean;
	readonly isManager: boolean;
	readonly hasSector: boolean;
	readonly hasMetadata: boolean;
	readonly hasDiploma: boolean;
	readonly hasCertificate: boolean;

	readonly cssPictureUrl: string;
	readonly isGhost: boolean;

	isCoachOf(user: IUser): boolean;

	ownTask(task: ITask): boolean;

	hasRightOnOrganization(
		organizationId: string,
		target: UserPartnerRightTarget,
		access: UserPartnerRightAccess
	): boolean;
}

export class User extends Entity implements IUser {
	constructor(
		id: string,
		role: UserRole,
		hasMfa: boolean,
		coachId: string,
		timeZone: string,
		isGhost: boolean,
		lastName: string,
		firstName: string,
		status: UserStatus,
		pictureUrl: string,
		emailAddress: string,
		phoneNumber: string,
		partnersId: string[],
		intercomHash: string,
		observersId: string[],
		organizationId: string,
		companySectors: string,
		metadata: UserMetadata,
		createTimestamp: number,
		updateTimestamp: number,
		partners: UserPartner[],
		diplomas: UserReference[],
		certificates: UserReference[],
		availabilities: UserAvailability[],
		modulePushes: UserModulePush[],
		coach$: Observable<IUser> = of(null),
		organization$: Observable<Organization> = of(null),
		partners$: Observable<Organization[]> = of([])
	) {
		super(id, createTimestamp, updateTimestamp);
		this._role = role;
		this._hasMfa = hasMfa;
		this._coachId = coachId;
		this._timeZone = timeZone;
		this._lastName = lastName;
		this._firstName = firstName;
		this._status = status;
		this._partners = partners;
		this._intercomHash = intercomHash;
		this._partnersId = ([...partnersId] || []).sort();
		this._pictureUrl = pictureUrl;
		this._observersId = ([...observersId] || []).sort();
		this._emailAddress = emailAddress;
		this._phoneNumber = phoneNumber;
		this._organizationId = organizationId;
		this._companySectors = companySectors;
		this._hasMetadata = !!metadata;
		this._metadata = metadata || new UserMetadata();
		this._diplomas = (diplomas || []).sort(
			(a, b) => b.startDate?.getTime() - a.startDate?.getTime()
		);
		this._certificates = (certificates || []).sort(
			(a, b) => b.startDate?.getTime() - a.startDate?.getTime()
		);
		this._availabilities = sortBy([...availabilities] || [], 'id');
		this._modulePushes = sortBy([...modulePushes] || [], 'contactId');
		this._coach$ = coach$;
		this._organization$ = organization$;
		this._partners$ = partners$;
		this._name = `${this._firstName} ${this._lastName}`;
		this._acronym = extractAcronym(this._name);
		this._sectors = (companySectors || '').split('').sort();
		this._lastReference =
			head(
				[...this._diplomas, ...this._certificates]
					.sort((a, b) => b.startDate?.getTime() - a.startDate?.getTime())
					.map((ref) => ref.summary)
			) || '-';
		this._isCoachee = role === UserRole.COACHEE;
		this._isManager = role === UserRole.MANAGER;
		this._isCoach = role === UserRole.COACH;
		this._isAdmin = role === UserRole.ADMIN;
		this._isGhost = isGhost;
	}

	private _role: UserRole;

	public get role(): UserRole {
		return this._role;
	}

	private _partners: UserPartner[];

	get partners(): UserPartner[] {
		return this._partners;
	}

	private _hasMfa: boolean;

	public get hasMfa(): boolean {
		return this._hasMfa;
	}

	private _coachId: string;

	public get coachId(): string {
		return this._coachId;
	}

	private _timeZone: string;

	public get timeZone(): string {
		return this._timeZone;
	}

	private _lastName: string;

	public get lastName(): string {
		return this._lastName;
	}

	private _firstName: string;

	public get firstName(): string {
		return this._firstName;
	}

	private _status: UserStatus;

	public get status(): UserStatus {
		return this._status;
	}

	private _pictureUrl: string;

	public get pictureUrl(): string {
		return this._pictureUrl;
	}

	private _phoneNumber: string;

	public get phoneNumber(): string {
		return this._phoneNumber;
	}

	private _emailAddress: string;

	public get emailAddress(): string {
		return this._emailAddress;
	}

	private _intercomHash: string;

	public get intercomHash(): string {
		return this._intercomHash;
	}

	private _partnersId: string[];

	public get partnersId(): string[] {
		return this._partnersId;
	}

	private _observersId: string[];

	public get observersId(): string[] {
		return this._observersId;
	}

	private _organizationId: string;

	public get organizationId(): string {
		return this._organizationId;
	}

	private _companySectors: string;

	public get companySectors(): string {
		return this._companySectors;
	}

	private _metadata: UserMetadata;

	public get metadata(): UserMetadata {
		return this._metadata;
	}

	private _diplomas: UserReference[];

	public get diplomas(): UserReference[] {
		return this._diplomas;
	}

	private _certificates: UserReference[];

	public get certificates(): UserReference[] {
		return this._certificates;
	}

	private _availabilities: UserAvailability[];

	public get availabilities(): UserAvailability[] {
		return this._availabilities;
	}

	private _modulePushes: UserModulePush[];

	public get modulePushes(): UserModulePush[] {
		return this._modulePushes;
	}

	private _coach$: Observable<IUser>;

	public get coach$(): Observable<IUser> {
		return this._coach$;
	}

	private _organization$: Observable<Organization>;

	public get organization$(): Observable<Organization> {
		return this._organization$;
	}

	private _partners$: Observable<Organization[]>;

	public get partners$(): Observable<Organization[]> {
		return this._partners$;
	}

	private _name: string;

	public get name(): string {
		return this._name;
	}

	private _acronym: string;

	public get acronym(): string {
		return this._acronym;
	}

	private _sectors: string[];

	public get sectors(): string[] {
		return this._sectors;
	}

	private _lastReference: string;

	public get lastReference(): string {
		return this._lastReference;
	}

	private _isCoachee: boolean;

	public get isCoachee(): boolean {
		return this._isCoachee;
	}

	private _isCoach: boolean;

	public get isCoach(): boolean {
		return this._isCoach;
	}

	private _isAdmin: boolean;

	public get isAdmin(): boolean {
		return this._isAdmin;
	}

	private _isManager: boolean;

	public get isManager(): boolean {
		return this._isManager;
	}

	private _hasMetadata: boolean;

	public get hasMetadata(): boolean {
		return this._hasMetadata;
	}

	private _isGhost: boolean;

	get isGhost(): boolean {
		return this._isGhost;
	}

	public get hasSector(): boolean {
		return !isEmpty(this._sectors);
	}

	public get hasDiploma(): boolean {
		return !isEmpty(this._diplomas);
	}

	public get hasCertificate(): boolean {
		return !isEmpty(this._certificates);
	}

	public get cssPictureUrl(): string {
		return this._pictureUrl ? `url(${this._pictureUrl})` : null;
	}

	public updateMoodFrequency(frequency: UserMoodFrequency) {
		this._metadata.updateMoodFrequency(frequency);
		return this;
	}

	public updateModulePushes(modulePushes: UserModulePush[]) {
		this._modulePushes = modulePushes;
		return this;
	}

	public hasRightOnOrganization(
		organizationId: string,
		target: UserPartnerRightTarget,
		access: UserPartnerRightAccess
	): boolean {
		if (this.role === 'ADMIN') {
			return true;
		}

		if (this.organizationId === organizationId) {
			return true;
		}

		const right = this.partners.find((p) => p.organizationId === organizationId);

		return (
			right?.rights.some((r) => r.target === target && r.access === access) ??
			false
		);
	}

	public isCoachOf(user: IUser): boolean {
		return user.coachId === this.id;
	}

	public ownTask(task: ITask): boolean {
		return task.ownerId === this.id;
	}
}
